/**
 * <s-bundle-tracker> mirrors the shopper's real cart for the Bundle & Save offer.
 * It only displays: count = eligible, one-time-purchase units; savings = the sum of
 * line discount allocations whose title starts with the configured prefix.
 * The Shopify discount is the source of truth for prices.
 */
const COLLAPSE_KEY = 'bundle-tracker:collapsed';
const LAST_COUNT_KEY = 'bundle-tracker:last-count';
const ELIGIBLE_KEY = 'bundle-tracker:eligible:';
const ELIGIBLE_TTL = 10 * 60 * 1000;
const POLL_MS = 3000;
const DEBOUNCE_MS = 800;
const CELEBRATE_MS = 2400;
const CART_EVENTS = [
  'shopify:cart:lines-update',
  'shopify:cart:update',
  'shopify:cart:discount-codes-update',
  'shopify:cart:note-update',
  'cart:updated',
  'cart:refresh',
  's-cart:opened',
];

const session = {
  get(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
  },
};

const fill = (template, values) => String(template || '').replace(
  /\[(count|max|percent|need|amount|product)\]/g,
  (match, key) => (values[key] ?? match)
);

class SBundleTracker extends HTMLElement {
  #ac;
  #pollId;
  #debounceId;
  #toastId;
  #celebrateId;
  #fetching = false;
  #eligibleIds = null;
  #lastCount = null;
  #lineKeys = [];
  #tiers = [];
  #max = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const d = this.dataset;

    this.#tiers = String(d.tiers || '')
      .split(',')
      .map((part) => part.split(':').map((value) => Number.parseFloat(value.trim())))
      .filter(([count, pct]) => Number.isFinite(count) && count > 0 && Number.isFinite(pct))
      .sort((a, b) => a[0] - b[0]);
    this.#max = this.#tiers.length ? this.#tiers[this.#tiers.length - 1][0] : 0;

    this.status = this.querySelector('[data-bt-status]');
    this.hint = this.querySelector('[data-bt-hint]');
    this.maxBlock = this.querySelector('[data-bt-max]');
    this.maxNote = this.querySelector('[data-bt-max-note]');
    this.savings = this.querySelector('[data-bt-savings]');
    this.toast = this.querySelector('[data-bt-toast]');
    this.panel = this.querySelector('[data-bt-panel]');
    this.pill = this.querySelector('[data-bt-pill]');
    this.pillText = this.querySelector('[data-bt-collapsed-text]');
    this.collapseButton = this.querySelector('[data-bt-collapse]');
    this.boxes = [...this.querySelectorAll('[data-bt-box]')];

    // Celebrate after a full-page add only when this session last saw the cart below max.
    const serverCount = Number.parseInt(d.count || '0', 10) || 0;
    const stored = session.get(LAST_COUNT_KEY);
    this.#lastCount = stored === null ? serverCount : Number.parseInt(stored, 10);
    if (this.#max && this.#lastCount < this.#max && serverCount >= this.#max) this.#celebrate();
    this.#lastCount = serverCount;
    session.set(LAST_COUNT_KEY, String(serverCount));

    this.#setCollapsed(session.get(COLLAPSE_KEY) === '1', false);
    this.#setupReveal();

    this.addEventListener('command', (event) => {
      if (event.command === '--collapse') this.#setCollapsed(true, true);
      if (event.command === '--expand') this.#setCollapsed(false, true);
      if (event.command === '--open-cart') this.#openCart();
    }, opts);

    for (const name of CART_EVENTS) {
      document.addEventListener(name, () => this.#schedule(150), opts);
    }
    document.addEventListener('submit', () => this.#schedule(DEBOUNCE_MS), { ...opts, capture: true });
    document.addEventListener('change', () => this.#schedule(DEBOUNCE_MS), { ...opts, capture: true });
    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-bundle-add]') : null;
      if (button) {
        event.preventDefault();
        this.#quickAdd(button);
      }
    }, opts);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refresh();
    }, opts);

    this.#pollId = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.refresh();
    }, POLL_MS);

    this.refresh();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearInterval(this.#pollId);
    window.clearTimeout(this.#debounceId);
    window.clearTimeout(this.#toastId);
    window.clearTimeout(this.#celebrateId);
  }

  #schedule(delay) {
    window.clearTimeout(this.#debounceId);
    this.#debounceId = window.setTimeout(() => this.refresh(), delay);
  }

  async #loadEligibleIds() {
    if (this.#eligibleIds) return this.#eligibleIds;
    const { collectionId, collectionUrl } = this.dataset;
    if (!collectionId || !collectionUrl) return null;

    const cacheKey = ELIGIBLE_KEY + collectionId;
    try {
      const cached = JSON.parse(session.get(cacheKey) || 'null');
      if (cached && Date.now() - cached.at < ELIGIBLE_TTL) {
        this.#eligibleIds = new Set(cached.ids);
        return this.#eligibleIds;
      }
    } catch { /* ignore bad cache */ }

    const ids = [];
    for (let page = 1; page <= 10; page += 1) {
      // products.json is the Ajax endpoint for a collection's product list; paged at 250.
      const response = await fetch(`${collectionUrl}/products.json?limit=250&page=${page}`, {
        signal: this.#ac.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const products = data?.products || [];
      for (const product of products) ids.push(product.id);
      if (products.length < 250) break;
    }
    this.#eligibleIds = new Set(ids);
    session.set(cacheKey, JSON.stringify({ at: Date.now(), ids }));
    return this.#eligibleIds;
  }

  async refresh() {
    if (this.#fetching || !this.#ac || this.#ac.signal.aborted) return null;
    this.#fetching = true;
    try {
      const eligible = await this.#loadEligibleIds();
      // cart.js returns the real cart, including Shopify's applied discount allocations.
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        signal: this.#ac.signal,
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const cart = await response.json();
      const state = this.#stateFromCart(cart, eligible);
      if (state) this.#render(state);
      return state;
    } catch {
      return null;
    } finally {
      this.#fetching = false;
    }
  }

  #stateFromCart(cart, eligible) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const prefix = String(this.dataset.prefix || '').trim().toLowerCase();
    let count = 0;
    let savings = 0;
    let original = 0;
    const thumbs = [];
    const keys = [];

    for (const item of items) {
      for (const allocation of item.line_level_discount_allocations || []) {
        const title = String(allocation?.discount_application?.title || '').toLowerCase();
        if (prefix && title.startsWith(prefix)) savings += Number(allocation.amount) || 0;
      }

      if (!eligible || !eligible.has(item.product_id) || item.selling_plan_allocation) continue;
      count += item.quantity;
      original += Number(item.original_line_price) || 0;
      keys.push(item.key);
      for (let i = 0; i < item.quantity && thumbs.length < this.#max; i += 1) {
        thumbs.push(this.#thumbUrl(item.image || item.featured_image?.url));
      }
    }

    if (!eligible) return null;
    return { count, savings, original, thumbs, keys, items };
  }

  #thumbUrl(src) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '160');
      return url.toString();
    } catch {
      return src;
    }
  }

  #tierInfo(count) {
    let percent = 0;
    let need = 0;
    let nextPercent = 0;
    for (const [tierCount, tierPercent] of this.#tiers) {
      if (count >= tierCount) {
        percent = tierPercent;
      } else if (!need) {
        need = tierCount - count;
        nextPercent = tierPercent;
      }
    }
    return { percent, need, nextPercent };
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const whole = cents % 100 === 0;
    const number = (value, decimals, thousands, decimal) => {
      const fixed = (value / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      if (key === 'amount_with_comma_separator') return number(cents, whole ? 0 : 2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return number(cents, 0, '.', ',');
      if (key === 'amount_with_apostrophe_separator') return number(cents, whole ? 0 : 2, "'", '.');
      if (key === 'amount_no_decimals') return number(cents, 0, ',', '.');
      return number(cents, whole ? 0 : 2, ',', '.');
    });
  }

  #render({ count, savings, original = 0, thumbs, keys }) {
    const d = this.dataset;
    const max = this.#max;
    const shown = Math.min(count, max);
    const { percent, need, nextPercent } = this.#tierInfo(count);
    const values = { count: shown, max, percent, need, amount: this.#formatMoney(savings) };
    const isMax = max > 0 && count >= max;
    this.#lineKeys = keys;

    const status = fill(d.status, values);
    if (this.status && this.status.textContent !== status) this.status.textContent = status;
    if (this.pillText) this.pillText.textContent = fill(d.collapsed, values);

    if (this.hint) {
      this.hint.textContent = fill(count === 0 ? d.hintEmpty : d.hintProgress, { ...values, percent: nextPercent });
      this.hint.classList.toggle('hidden', isMax);
    }
    if (this.maxBlock) {
      this.maxBlock.classList.toggle('hidden', !isMax);
      this.maxBlock.classList.toggle('flex', isMax);
    }
    if (this.maxNote) {
      const maxPercent = this.#tiers.length ? this.#tiers[this.#tiers.length - 1][1] : 0;
      this.maxNote.textContent = fill(d.maxNote, { ...values, percent: maxPercent });
    }
    if (this.savings) {
      const savingsText = this.savings.querySelector('[data-bt-savings-text]');
      const was = this.savings.querySelector('[data-bt-was]');
      const now = this.savings.querySelector('[data-bt-now]');
      if (savingsText) savingsText.textContent = fill(d.savings, values);
      if (was) was.textContent = this.#formatMoney(original);
      if (now) now.textContent = this.#formatMoney(Math.max(0, original - savings));
      this.savings.classList.toggle('hidden', savings <= 0);
      this.savings.classList.toggle('flex', savings > 0);
    }

    this.boxes.forEach((box, index) => {
      const thumb = box.querySelector('[data-bt-thumb]');
      const label = box.querySelector('[data-bt-box-label]');
      const src = thumbs[index] || '';
      const filled = index < shown;
      if (!thumb) return;
      let img = thumb.querySelector('img');
      if (filled && src) {
        if (!img) {
          img = document.createElement('img');
          img.alt = '';
          img.width = 80;
          img.height = 80;
          img.className = 'size-full object-contain';
          thumb.append(img);
        }
        if (img.getAttribute('src') !== src) img.src = src;
      } else if (img && !filled) {
        img.remove();
      }
      thumb.classList.toggle('bg-(--bt-box)', filled);
      thumb.classList.toggle('outline-2', filled);
      thumb.classList.toggle('outline-(--bt-gold)', filled);
      thumb.classList.toggle('bg-(--bt-box)/15', !filled);
      thumb.classList.toggle('outline-1', !filled);
      thumb.classList.toggle('outline-dashed', !filled);
      thumb.classList.toggle('outline-(--bt-box)/50', !filled);
      box.querySelector('[data-bt-empty]')?.classList.toggle('hidden', filled);
      label?.classList.toggle('invisible', !filled || !label.textContent.trim());
    });

    const previous = this.#lastCount;
    this.#lastCount = count;
    session.set(LAST_COUNT_KEY, String(count));
    if (max && previous !== null && previous < max && count >= max) {
      this.#celebrate();
    }
    this.lastValues = values;
  }

  #celebrate() {
    if (this.hasAttribute('data-celebrating')) return;
    if (this.pill && !this.pill.classList.contains('hidden')) this.#setCollapsed(false, false);
    this.setAttribute('data-celebrating', '');
    this.#showToast(this.dataset.celebrate);
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#sparkle();
    window.clearTimeout(this.#celebrateId);
    this.#celebrateId = window.setTimeout(() => this.removeAttribute('data-celebrating'), CELEBRATE_MS);
  }

  #sparkle() {
    if (!this.panel) return;
    for (let i = 0; i < 14; i += 1) {
      const spark = document.createElement('span');
      const angle = (Math.PI * 2 * i) / 14;
      const distance = 50 + Math.random() * 60;
      spark.setAttribute('data-bt-spark', '');
      spark.setAttribute('aria-hidden', 'true');
      spark.className = 'pointer-events-none absolute left-1/2 top-1/2 size-2 rounded-full bg-(--bt-gold)';
      spark.style.setProperty('--bt-dx', `${Math.cos(angle) * distance}px`);
      spark.style.setProperty('--bt-dy', `${Math.sin(angle) * distance}px`);
      spark.style.animationDelay = `${(i % 4) * 80}ms`;
      this.panel.append(spark);
      window.setTimeout(() => spark.remove(), CELEBRATE_MS);
    }
  }

  #showToast(message) {
    if (!this.toast || !message) return;
    this.toast.textContent = message;
    this.toast.classList.remove('opacity-0');
    window.clearTimeout(this.#toastId);
    this.#toastId = window.setTimeout(() => {
      this.toast.classList.add('opacity-0');
      window.setTimeout(() => {
        if (this.toast.classList.contains('opacity-0')) this.toast.textContent = '';
      }, 300);
    }, 3200);
  }

  #setCollapsed(collapsed, persist) {
    if (!this.panel || !this.pill) return;
    this.panel.classList.toggle('hidden', collapsed);
    this.pill.classList.toggle('hidden', !collapsed);
    this.pill.classList.toggle('flex', collapsed);
    this.pill.setAttribute('aria-expanded', String(!collapsed));
    this.collapseButton?.setAttribute('aria-expanded', String(!collapsed));
    if (persist) {
      session.set(COLLAPSE_KEY, collapsed ? '1' : '0');
      (collapsed ? this.pill : this.collapseButton)?.focus();
    }
  }

  #setupReveal() {
    const anchorId = this.dataset.revealAnchor;
    if (!anchorId) return;
    const anchor = document.getElementById(anchorId);
    if (!anchor || !('IntersectionObserver' in window)) {
      this.hidden = false;
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top < 0)) {
        this.hidden = false;
        observer.disconnect();
      }
    });
    observer.observe(anchor);
    this.#ac.signal.addEventListener('abort', () => observer.disconnect());
  }

  async #openCart() {
    const dialog = document.getElementById('cart-drawer');
    try {
      if (window.Shopify?.actions?.openCart) {
        await window.Shopify.actions.openCart();
      } else if (dialog instanceof HTMLDialogElement) {
        if (!dialog.open) dialog.showModal();
      }
    } catch { /* fall through to the cart page */ }

    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) {
      window.location.href = this.dataset.cartUrl;
      return;
    }
    this.#highlightLines(dialog);
  }

  #highlightLines(dialog) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const gold = getComputedStyle(this).getPropertyValue('--bt-gold').trim() || 'gold';
    window.setTimeout(() => {
      for (const key of new Set(this.#lineKeys)) {
        const line = dialog.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`);
        line?.animate(
          [
            { backgroundColor: 'transparent', boxShadow: 'inset 3px 0 0 transparent' },
            { backgroundColor: `color-mix(in srgb, ${gold} 22%, transparent)`, boxShadow: `inset 3px 0 0 ${gold}` },
            { backgroundColor: 'transparent', boxShadow: 'inset 3px 0 0 transparent' },
          ],
          { duration: 1600, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
        );
      }
    }, 250);
  }

  async #quickAdd(button) {
    const variantId = String(button.getAttribute('data-bundle-add') || '').trim();
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      if (window.Shopify?.actions?.updateCart) {
        // Theme handler refreshes the drawer partials in the background without opening it.
        await window.Shopify.actions.updateCart({ lines: [{ merchandiseId: variantId, quantity: 1 }] });
      } else {
        await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
      }
      let state = null;
      for (let attempt = 0; attempt < 3 && !state; attempt += 1) {
        state = await this.refresh();
        if (!state) await new Promise((resolve) => window.setTimeout(resolve, 300));
      }
      if (state && !this.hasAttribute('data-celebrating')) {
        const line = state.items.find((item) => String(item.variant_id) === variantId);
        this.#showToast(fill(this.dataset.added, { ...this.lastValues, product: line?.product_title || '' }));
      }
    } catch { /* the theme's cart feedback reports add errors */ }
    finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-bundle-tracker')) {
  customElements.define('s-bundle-tracker', SBundleTracker);
}
